import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Chip from '@material-ui/core/Chip';
import { makeStyles } from '@material-ui/core/styles';
import { mdiCreation, mdiTagPlusOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { selectSelectedFilePermissionId } from 'scenes/ProjectGallery/store/selectors/project-gallery-base.selectors';
import { selectSelectedFilepermissionMediaTags } from 'store/mediaTags/selectors/mediaTags.selectors';
import { isAiTag } from '../ImageMediaTags/MediaTagChip';
import {
  VIEWER_OVERLAY_GUARDS,
  getMediaTagLabel,
  orderMediaTagsForRow,
} from './mobileMediaTags';

/** The widest a chip gets; a longer name ends in an ellipsis. */
export const MOBILE_TAG_CHIP_MAX_WIDTH = 160;

const CHIP_HEIGHT = 28;
const CHIP_GAP = 4;

/**
 * How far a chip's touch target reaches above and below it, into the
 * scroller's padding: a 28px chip is a target of 36px for a finger.
 */
const TOUCH_REACH = 4;

const useStyles = makeStyles((theme) => ({
  root: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: CHIP_GAP,
    minWidth: 0,
    pointerEvents: 'auto',
    // In from the photo's edges as the comments are, and clear of a notch.
    paddingLeft: 'max(2px, env(safe-area-inset-left, 0px))',
    paddingRight: 'max(2px, env(safe-area-inset-right, 0px))',
  },
  // Scrolls sideways and only sideways: a swipe there moves the chips and
  // neither pages the photo nor scrolls the page. No scrollbar over the
  // photo.
  scroller: {
    flex: '1 1 auto',
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: CHIP_GAP,
    overflowX: 'auto',
    overflowY: 'hidden',
    touchAction: 'pan-x',
    overscrollBehaviorX: 'contain',
    WebkitOverflowScrolling: 'touch',
    padding: `${TOUCH_REACH}px 0`,
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  // Dark and see-through over the photo, like the comments and the comment
  // input under the row, and never squeezed by the chips beside it.
  chip: {
    position: 'relative',
    flexShrink: 0,
    maxWidth: MOBILE_TAG_CHIP_MAX_WIDTH,
    height: CHIP_HEIGHT,
    fontFamily: theme.typography.fontPanton.fontFamily,
    fontSize: '0.75rem',
    color: '#fff',
    backgroundColor: 'rgba(0,0,0,0.55)',
    border: '1px solid rgba(255,255,255,0.35)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -TOUCH_REACH,
      bottom: -TOUCH_REACH,
      left: 0,
      right: 0,
    },
  },
  // In place of MUI's light grey on hover and focus.
  clickable: {
    '&:hover, &:focus': {
      backgroundColor: 'rgba(0,0,0,0.7)',
    },
  },
  label: {
    paddingLeft: 10,
    paddingRight: 10,
  },
  icon: {
    flexShrink: 0,
    color: 'inherit',
    marginLeft: 7,
    marginRight: -3,
  },
  // The '+' chip while the photo has tags: the glyph alone.
  iconAlone: {
    flexShrink: 0,
    color: 'inherit',
    marginLeft: 8,
    marginRight: 8,
  },
  labelNone: {
    padding: 0,
  },
}));

/**
 * The photo's media tags on a phone-sized screen (below md): one row of
 * small chips along the bottom of the photo, above the comment input
 * (CommentsSection's `overlayAccessory`), or on its own at the bottom where
 * the viewer has no comments. The viewer decides where it shows
 * (showsMobileTagRow) and opens the sheets it asks for.
 *
 * A '+' chip on the left, outside the part that scrolls so it is always in
 * reach, asks for the sheet that adds a tag (MobileAddMediaTagSheet). It says
 * "Lisää tunniste" while the photo has no tags and is its glyph alone once
 * there are some, keeping that name for a screen reader. Then the tags: a
 * person's own first and the AI tagger's, with its sparkle, after them. A
 * long name ends in an ellipsis, and a tap on the chip asks for the sheet
 * that names the tag in full and removes it (MobileMediaTagSheet). The
 * chips scroll sideways under a finger and under a mouse wheel, and every
 * photo's start from the left.
 *
 * What happens on the row stays there (VIEWER_OVERLAY_GUARDS): a swipe on it
 * never pages the photo, and in a narrow desktop window, where the viewer
 * runs its desktop handlers, a click on a chip does not zoom the photo. A
 * pinch that starts on it still zooms.
 * @param {Object} props
 * @param {Function} props.onAddTag the '+' chip was tapped
 * @param {Function} props.onOpenTag a tag's chip was tapped; called with the tag link's id
 */
export function MobileMediaTagRow({ onAddTag, onOpenTag }) {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const tags = useSelector(selectSelectedFilepermissionMediaTags);
  const selectedFileId = useSelector(selectSelectedFilePermissionId);
  const orderedTags = useMemo(() => orderMediaTagsForRow(tags), [tags]);
  const hasTags = orderedTags.length > 0;

  const scrollerRef = useRef(null);

  useLayoutEffect(() => {
    scrollerRef.current.scrollLeft = 0;
  }, [selectedFileId]);

  // A mouse wheel turns vertically, and the row scrolls sideways; while the
  // row has somewhere to go, the turn is the row's alone. A listener of its
  // own: React's wheel listener sits on the document, where the browser
  // makes it passive, so it could not keep the turn from the page.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const handleWheel = (event) => {
      // A trackpad's sideways swipe scrolls the row by itself.
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      const max = scroller.scrollWidth - scroller.clientWidth;
      const next = Math.max(
        0,
        Math.min(max, scroller.scrollLeft + event.deltaY)
      );
      if (next === scroller.scrollLeft) return;
      scroller.scrollLeft = next;
      event.preventDefault();
    };
    scroller.addEventListener('wheel', handleWheel, { passive: false });
    return () => scroller.removeEventListener('wheel', handleWheel);
  }, []);

  const chipClasses = {
    root: classes.chip,
    clickable: classes.clickable,
    label: classes.label,
    icon: classes.icon,
  };

  return (
    <div
      {...VIEWER_OVERLAY_GUARDS}
      role="group"
      aria-label={t('gallery.tags')}
      className={classes.root}
      data-viewer-tag-row="true"
    >
      <Chip
        clickable
        classes={{
          ...chipClasses,
          label: hasTags ? classes.labelNone : classes.label,
          icon: hasTags ? classes.iconAlone : classes.icon,
        }}
        icon={<Icon path={mdiTagPlusOutline} size="18px" />}
        label={hasTags ? null : t('gallery.add_tag')}
        aria-label={t('gallery.add_tag')}
        title={hasTags ? t('gallery.add_tag') : undefined}
        aria-haspopup="dialog"
        data-viewer-add-tag="true"
        onClick={onAddTag}
      />
      <div ref={scrollerRef} className={classes.scroller}>
        {orderedTags.map((tag) => {
          const isAi = isAiTag(tag);
          return (
            <Chip
              key={tag.id}
              clickable
              classes={chipClasses}
              icon={isAi ? <Icon path={mdiCreation} size="14px" /> : undefined}
              label={getMediaTagLabel(tag)}
              aria-haspopup="dialog"
              data-viewer-tag={tag.id}
              data-ai-tag={isAi ? 'true' : undefined}
              onClick={() => onOpenTag(tag.id)}
            />
          );
        })}
      </div>
    </div>
  );
}

MobileMediaTagRow.propTypes = {
  /** The '+' chip was tapped. */
  onAddTag: PropTypes.func.isRequired,
  /** A tag's chip was tapped; called with the tag link's id. */
  onOpenTag: PropTypes.func.isRequired,
};

export default MobileMediaTagRow;
